import { useQueryClient } from "@tanstack/react-query";
import classNames from "classnames";
import { Button } from "../../../components/Button/Button.tsx";
import { PimentoStar } from "../../../components/Icon/assets/PimentoStar.tsx";
import { useOnMount } from "../../../hooks/useOnMount.ts";
import { upgradeDialogStore } from "../../components/UpgradeDialog/upgradeDialogStore.ts";
import { useIsUserWorkspaceOwner } from "../../hooks/useIsUserWorkspaceOwner.ts";
import { useUser } from "../../hooks/useUser.ts";
import { useWorkspace } from "../../hooks/useWorkspace.ts";
import { profileSettingsDialogStore } from "../ProfileSettings/stores/profileSettingsDialogStore.ts";
const WARNING_THRESHOLD = 20;

export const BalanceCallout = () => {
  const { user } = useUser();
  const { workspace } = useWorkspace();

  const isUserWorkspaceOwner = useIsUserWorkspaceOwner();

  const queryClient = useQueryClient();
  useOnMount(() => {
    // XXX: Invalidate the workspaces query when the component mounts
    // This is to ensure that the balance displayed to users is always up to date
    void queryClient.invalidateQueries({
      queryKey: [`workspaces/${workspace.uuid}`],
    });
  });

  if (!user) return null;

  // FIXME: This component should be removed in favor of the new billing detail page in workspace settings
  switch (workspace.type) {
    case "enterprise":
      return (
        <div className="rounded-200 bg-primary p-400 flex-col gap-300">
          <div className="flex-row items-center justify-between">
            <div className="label-lg-semibold">Balance</div>
          </div>
          <div className="flex-row items-center gap-150">
            <div
              className={classNames(
                "w-400 h-400 rounded-full flex-col-center",
                workspace.total_credits_remaining_amount < WARNING_THRESHOLD
                  ? "bg-[var(--red-14)]"
                  : "bg-surface-emphasis-primary-rest",
              )}
            >
              <PimentoStar size={8} className="fill-white" />
            </div>
            <div
              className={classNames(
                "label-lg-semibold",
                workspace.total_credits_remaining_amount < WARNING_THRESHOLD
                  ? "text-[var(--red-14)]"
                  : "",
              )}
            >
              {workspace.total_credits_remaining_amount}
              {workspace.total_credits_remaining_amount > 1
                ? " credits"
                : " credit"}{" "}
              left
            </div>
          </div>
          <div className="pt-200 flex-col gap-100">
            <Button
              size="sm"
              variant={
                workspace.total_credits_remaining_amount < WARNING_THRESHOLD
                  ? "tertiary"
                  : "secondary"
              }
              onClick={() => {
                window.open("mailto:contact@pimento.design", "_blank");
              }}
              className="w-full"
            >
              Contact support
            </Button>
          </div>
        </div>
      );
    case "basic":
    case "team":
    case "premium":
    case "pro":
      return (
        <div className="rounded-200 bg-primary p-400 flex-col gap-300">
          <div className="flex-row items-center justify-between">
            <div className="label-lg-semibold">Balance</div>
          </div>
          <div className="flex-row items-center gap-150">
            <div
              className={classNames(
                "w-400 h-400 rounded-full flex-col-center",
                workspace.total_credits_remaining_amount < WARNING_THRESHOLD
                  ? "bg-[var(--red-14)]"
                  : "bg-surface-emphasis-primary-rest",
              )}
            >
              <PimentoStar size={8} className="fill-white" />
            </div>
            <div
              className={classNames(
                "label-lg-semibold",
                workspace.total_credits_remaining_amount < WARNING_THRESHOLD
                  ? "text-[var(--red-14)]"
                  : "",
              )}
            >
              {workspace.total_credits_remaining_amount}
              {workspace.total_credits_remaining_amount !== 1
                ? " credits"
                : " credit"}{" "}
              left
            </div>
          </div>
          {workspace.type !== "basic" &&
            workspace.total_credits_remaining_amount < WARNING_THRESHOLD && (
              <div className="label-sm-default">
                Seamlessly continue consuming credits with our usage-based
                billing
              </div>
            )}
          {isUserWorkspaceOwner &&
            (workspace.type === "basic" ? (
              <div className="pt-200 flex-col">
                <Button
                  size="sm"
                  variant="primary"
                  onClick={() => {
                    upgradeDialogStore.openDialog();
                  }}
                >
                  Upgrade subscription
                </Button>
              </div>
            ) : (
              <div className="pt-200 flex-col">
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => {
                    profileSettingsDialogStore.openDialog();
                    profileSettingsDialogStore.setProfileSettingsTab("plan");
                  }}
                >
                  Manage subscription
                </Button>
              </div>
            ))}
        </div>
      );
  }
};
